import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import type { PlaceBasketOrdersResult } from "../../../functions/src/types";
import type { BasketCheckoutUrgencyState } from "../../../functions/src/common/checkoutDeadlineUrgency";
import { FULFILLMENT_RETRY_BUDGET_MS } from "../../../functions/src/api/constants";
import { isExpectedOrderDeadlineCallableError } from "../../../functions/src/common/orderDeadlines";
import { createOrderPayment, getCallableErrorMessage, getCheckoutExpiredDates, initiateOrderCheckout, isBasketFulfillmentRetryable, isCheckoutRemoveExpiredDaysError, isCheckoutResumeFulfillmentError, isFulfillmentTransientError, placeBasketOrders, useBalance, useOrderCheckoutSession } from "../../api/funds";
import { isCallableTransportError } from "../../api/callableRetry";
import type { OrderCheckoutSession } from "../../../functions/src/types";
import { langContext } from "../../api/Lang";
import { captureException } from "../../api/sentry";
import YesNoDlg from "../../api/YesNoDlg";
import { useStrings } from "../../common-strings";
import BasketDrawer from "./BasketDrawer";
import BasketPaymentResult from "./BasketPaymentResult";
import { clearAbandonedOrderCheckoutSession, removeExpiredDaysFromBasket, shouldRequoteStaleCheckoutSession } from "./basketActions";
import { useBasketStrings } from "./basket-strings";
import { useBasketEntries, type BasketState } from "./useBasket";
import { useBasketCheckoutUrgency } from "./useBasketCheckoutUrgency";

export type BasketFulfillment = {
    status: 'processing' | 'done' | 'error';
    /** True when a Viva payment preceded registration (controls the overlay title). */
    paid: boolean;
    result?: PlaceBasketOrdersResult;
    errorMessage?: string;
};

type BasketContextValue = BasketState & {
    isOpen: boolean;
    openBasket: () => void;
    closeBasket: () => void;
    /** Order screen reads this and switches the selected day (then clears it). */
    focusOrderDate: string | null;
    goToOrderDate: (date: string) => void;
    clearFocusOrderDate: () => void;
    /** State 1 — Viva failed: inline alert inside the drawer. */
    basketError: string | null;
    setBasketError: (err: string | null) => void;
    /** State 2 / success — payment result overlay (placeBasketOrders outcome). */
    fulfillment: BasketFulfillment | null;
    runFulfillment: (paid: boolean) => Promise<void>;
    retryFulfillment: () => Promise<void>;
    dismissFulfillment: () => Promise<void>;
    clearFulfillment: () => void;
    /** True while basket Pay or empty-basket mutates RTDB (blocks order screen edits too). */
    basketLocked: boolean;
    setBasketLocked: (locked: boolean) => void;
    /** Server rejected checkout — prompt to auto-remove expired basket days, then retry `resume`. */
    offerRemoveExpiredDays: (error: unknown, resume: () => Promise<void>) => boolean;
    checkoutUrgency: BasketCheckoutUrgencyState;
    checkoutUrgencyDismissed: boolean;
    dismissCheckoutUrgency: () => void;
};

export const basketContext = createContext<BasketContextValue>({
    entries: [],
    staleEntries: [],
    staleGroups: [],
    totalBasketDays: 0,
    entriesLoading: false,
    modelConstants: null,
    basketServerNow: undefined,
    isOpen: false,
    openBasket: () => undefined,
    closeBasket: () => undefined,
    focusOrderDate: null,
    goToOrderDate: () => undefined,
    clearFocusOrderDate: () => undefined,
    basketError: null,
    setBasketError: () => undefined,
    fulfillment: null,
    runFulfillment: async () => undefined,
    retryFulfillment: async () => undefined,
    dismissFulfillment: async () => undefined,
    clearFulfillment: () => undefined,
    basketLocked: false,
    setBasketLocked: () => undefined,
    offerRemoveExpiredDays: () => false,
    checkoutUrgency: { active: false },
    checkoutUrgencyDismissed: false,
    dismissCheckoutUrgency: () => undefined,
});

export function useBasketContext(): BasketContextValue {
    return useContext(basketContext);
}

export default function BasketProvider({ children }: { children: React.ReactNode }) {
    const [lang] = useContext(langContext);
    const strings = useBasketStrings();
    const commonStrings = useStrings();
    const basketState = useBasketEntries();
    const { checkoutUrgency, checkoutUrgencyDismissed, dismissCheckoutUrgency } = useBasketCheckoutUrgency(
        basketState.entries,
        basketState.entriesLoading,
        lang,
        basketState.basketServerNow,
    );
    const [isOpen, setIsOpen] = useState(false);
    const [focusOrderDate, setFocusOrderDate] = useState<string | null>(null);
    const [basketError, setBasketError] = useState<string | null>(null);
    const [fulfillment, setFulfillment] = useState<BasketFulfillment | null>(null);
    const [basketLocked, setBasketLocked] = useState(false);
    const [expiredDaysPrompt, setExpiredDaysPrompt] = useState<{ message: string; expiredDates: string[]; resume: () => Promise<void> } | null>(null);
    const [removingExpiredDays, setRemovingExpiredDays] = useState(false);
    const session = useOrderCheckoutSession();
    const balance = useBalance();
    const retryFulfillmentRef = useRef<() => Promise<void>>(() => Promise.resolve());

    const openBasket = useCallback(() => setIsOpen(true), []);
    const closeBasket = useCallback(() => setIsOpen(false), []);
    const goToOrderDate = useCallback((date: string) => {
        if (basketLocked) return;
        setFocusOrderDate(date);
        setIsOpen(false);
    }, [basketLocked]);
    const clearFocusOrderDate = useCallback(() => setFocusOrderDate(null), []);
    const clearFulfillment = useCallback(() => setFulfillment(null), []);

    const offerRemoveExpiredDays = useCallback((error: unknown, resume: () => Promise<void>): boolean => {
        if (!isCheckoutRemoveExpiredDaysError(error)) {
            return false;
        }
        setExpiredDaysPrompt({
            message: getCallableErrorMessage(error, strings.checkoutFailedTryAgain),
            expiredDates: getCheckoutExpiredDates(error),
            resume,
        });
        setIsOpen(true);
        return true;
    }, [strings.checkoutFailedTryAgain]);

    const onConfirmRemoveExpiredDays = useCallback(async () => {
        if (!expiredDaysPrompt) return;
        const { expiredDates, resume } = expiredDaysPrompt;
        setRemovingExpiredDays(true);
        setBasketError(null);
        try {
            await removeExpiredDaysFromBasket(expiredDates, [
                ...basketState.entries.map(e => ({ date: e.date, kind: e.kind })),
                ...basketState.staleEntries.map(e => ({ date: e.date, kind: e.kind })),
            ]);
            await clearAbandonedOrderCheckoutSession(session ?? null);
            setExpiredDaysPrompt(null);
            await resume();
        } catch (error) {
            console.error('removeExpiredDaysFromBasket failed:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'removeExpiredDaysFromBasket' });
            setBasketError(getCallableErrorMessage(error, strings.checkoutFailedTryAgain));
        } finally {
            setRemovingExpiredDays(false);
        }
    }, [expiredDaysPrompt, basketState.entries, basketState.staleEntries, session, strings.checkoutFailedTryAgain]);

    const runFulfillment = useCallback(async (paid: boolean) => {
        setFulfillment({ status: 'processing', paid });
        // Paid: keep retrying long enough to outlast a stuck processingAt lock (webhook worker crashed
        // mid-fulfillment) so we take over instead of showing "already being registered" with money debited.
        const retryDeadline = Date.now() + (paid ? FULFILLMENT_RETRY_BUDGET_MS : 6000);
        const retryDelayMs = 2000;
        for (; ;) {
            try {
                const result = await placeBasketOrders(lang);
                setFulfillment({ status: 'done', paid, result });
                return;
            } catch (error) {
                const shouldRetry = Date.now() + retryDelayMs < retryDeadline && isBasketFulfillmentRetryable(error, paid);
                if (shouldRetry) {
                    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                    continue;
                }
                console.error('placeBasketOrders failed:', error);
                if (offerRemoveExpiredDays(error, () => retryFulfillmentRef.current())) {
                    setBasketLocked(false);
                    setFulfillment(null);
                    return;
                }
                if (!isCallableTransportError(error) && !isExpectedOrderDeadlineCallableError(error) && !isFulfillmentTransientError(error) && !isCheckoutRemoveExpiredDaysError(error)) {
                    captureException(error instanceof Error ? error : new Error(String(error)), { context: 'runBasketFulfillment' });
                }
                setFulfillment({ status: 'error', paid, errorMessage: getCallableErrorMessage(error, 'Failed to register orders') });
                return;
            }
        }
    }, [lang, offerRemoveExpiredDays]);

    /** Credits-only retry re-quotes the basket; paid retries re-quote when wallet now covers the total. */
    const retryFulfillment = useCallback(async () => {
        const paid = fulfillment?.paid ?? false;
        const dates = basketState.entries.map(e => e.date);
        const tryRequote = async (checkoutSession: OrderCheckoutSession | null | undefined): Promise<boolean> => {
            if (!dates.length || !checkoutSession) return false;
            if (!shouldRequoteStaleCheckoutSession(checkoutSession, balance ?? 0)) return false;
            try {
                const quote = await initiateOrderCheckout(dates, lang);
                if (quote.payAmount > 0) {
                    await createOrderPayment(quote.payAmount, 'orderCheckout');
                    return true;
                }
            } catch (error) {
                if (!isCheckoutResumeFulfillmentError(error)) {
                    throw error;
                }
            }
            return true;
        };

        if (basketState.entries.length > 0) {
            try {
                if (!paid) {
                    const quote = await initiateOrderCheckout(dates, lang);
                    if (quote.payAmount > 0) {
                        await createOrderPayment(quote.payAmount, 'orderCheckout');
                        return;
                    }
                } else if (await tryRequote(session)) {
                    await runFulfillment(false);
                    return;
                }
            } catch (error) {
                if (offerRemoveExpiredDays(error, () => retryFulfillment())) {
                    setBasketLocked(false);
                    setFulfillment(null);
                    return;
                }
                if (!isCheckoutResumeFulfillmentError(error)) {
                    setFulfillment({
                        status: 'error',
                        paid,
                        errorMessage: getCallableErrorMessage(error, 'Failed to register orders'),
                    });
                    return;
                }
            }
        }
        await runFulfillment(paid);
    }, [fulfillment?.paid, basketState.entries, runFulfillment, session, balance, lang, offerRemoveExpiredDays]);

    retryFulfillmentRef.current = retryFulfillment;

    const dismissFulfillment = useCallback(async () => {
        setBasketLocked(false);
        setFulfillment(null);
        await clearAbandonedOrderCheckoutSession(session ?? null);
    }, [session]);

    return (
        <basketContext.Provider value={{
            ...basketState,
            isOpen,
            openBasket,
            closeBasket,
            focusOrderDate,
            goToOrderDate,
            clearFocusOrderDate,
            basketError,
            setBasketError,
            fulfillment,
            runFulfillment,
            retryFulfillment,
            dismissFulfillment,
            clearFulfillment,
            basketLocked,
            setBasketLocked,
            offerRemoveExpiredDays,
            checkoutUrgency,
            checkoutUrgencyDismissed,
            dismissCheckoutUrgency,
        }}>
            {children}
            {expiredDaysPrompt &&
                <YesNoDlg
                    title={strings.expiredDaysTitle}
                    msg={expiredDaysPrompt.message}
                    yes={commonStrings.yes}
                    no={commonStrings.no}
                    yesLoading={removingExpiredDays}
                    onNo={() => {
                        if (removingExpiredDays) return;
                        setExpiredDaysPrompt(null);
                        setBasketLocked(false);
                    }}
                    onYes={() => void onConfirmRemoveExpiredDays()}
                />
            }
            <BasketDrawer />
            {fulfillment && <BasketPaymentResult />}
        </basketContext.Provider>
    );
}
